import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Calendar } from '@freelancer-os/ui/src/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@freelancer-os/ui/src/components/ui/card';
import { Input } from '@freelancer-os/ui/src/components/ui/input';
import { Label } from '@freelancer-os/ui/src/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@freelancer-os/ui/src/components/ui/popover';
import { cn } from '@freelancer-os/ui/src/lib/utils';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { ArrowLeft, CalendarIcon, Plus, Trash } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useClients } from '../../hooks/useClients';
import { useCreateInvoice } from '../../hooks/useInvoices';
import { useChatStore } from '../../stores/chatStore';

export function InvoiceFormPage() {
  const { t } = useTranslation('invoices');
  const navigate = useNavigate();
  const { data: clients } = useClients();
  const { mutate: createInvoice, isPending } = useCreateInvoice();

  const hydratedRef = useRef(false);
  const initialHydration = useChatStore.getState().pendingHydration;

  const [formData, setFormData] = useState(() => {
    if (initialHydration && !hydratedRef.current) {
      hydratedRef.current = true;
      const { invoiceDraft } = initialHydration;
      useChatStore.getState().setPendingHydration(null);
      return {
        clientId: invoiceDraft.clientId ?? '',
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        taxRate: invoiceDraft.taxRate ?? 0,
        depositAmount: invoiceDraft.depositAmount ?? 0,
        notes: invoiceDraft.notes ?? '',
      };
    }
    return {
      clientId: '',
      dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      taxRate: 0,
      depositAmount: 0,
      notes: '',
    };
  });

  const [items, setItems] = useState(() => {
    if (initialHydration?.invoiceDraft.items?.length) {
      return initialHydration.invoiceDraft.items.map((item, index) => ({
        id: Date.now() + index,
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      }));
    }
    return [{ id: 1, description: '', quantity: 1, unitPrice: 0 }];
  });

  const handleAddItem = () => {
    setItems([...items, { id: Date.now(), description: '', quantity: 1, unitPrice: 0 }]);
  };

  const handleRemoveItem = (id: number) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const handleItemChange = (id: number, field: string, value: string | number) => {
    setItems(items.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const subTotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const taxAmount = (subTotal * formData.taxRate) / 100;
  const total = subTotal + taxAmount;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) {
      alert(t('form.selectClientAlert'));
      return;
    }

    createInvoice(
      {
        clientId: formData.clientId,
        dueDate: formData.dueDate.toISOString(),
        taxRate: formData.taxRate,
        depositAmount: formData.depositAmount,
        notes: formData.notes,
        items: items.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
      },
      {
        onSuccess: () => navigate('/invoices'),
      },
    );
  };

  return (
    <div className="p-8 max-w-4xl mx-auto w-full">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">{t('form.title')}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Details */}
        <Card>
          <CardHeader>
            <CardTitle>{t('form.invoiceDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="clientId">{t('form.client')}</Label>
              <select
                id="clientId"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                required
              >
                <option value="" disabled>
                  {t('form.selectClient')}
                </option>
                {clients?.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>{t('form.dueDate')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal h-10',
                      !formData.dueDate && 'text-muted-foreground',
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.dueDate
                      ? format(formData.dueDate, 'dd/MM/yyyy', { locale: vi })
                      : t('form.selectDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="max-w-[300px] p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.dueDate}
                    onSelect={(date) =>
                      date && setFormData({ ...formData, dueDate: date })
                    }
                    locale={vi}
                    weekStartsOn={1}
                    className="p-4 w-full"
                    formatters={{
                      formatWeekdayName: (date) => {
                        const day = date.getDay();
                        return ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][day]!;
                      },
                    }}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{t('form.lineItems')}</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
              <Plus className="h-4 w-4 mr-2" /> {t('form.addItem')}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {items.map((item, index) => (
              <div key={item.id} className="flex gap-4 items-start">
                <div className="flex-1 space-y-2">
                  <Label className={index !== 0 ? 'sr-only' : ''}>{t('form.description')}</Label>
                  <Input
                    placeholder="Project milestone..."
                    value={item.description}
                    onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                    required
                  />
                </div>
                <div className="w-24 space-y-2">
                  <Label className={index !== 0 ? 'sr-only' : ''}>{t('form.qty')}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(item.id, 'quantity', Number(e.target.value))}
                    required
                  />
                </div>
                <div className="w-32 space-y-2">
                  <Label className={index !== 0 ? 'sr-only' : ''}>{t('form.unitPrice')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={item.unitPrice}
                    onChange={(e) => handleItemChange(item.id, 'unitPrice', Number(e.target.value))}
                    required
                  />
                </div>
                <div className="pt-8">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemoveItem(item.id)}
                    disabled={items.length === 1}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Totals & Notes */}
        <div className="grid gap-8 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('form.additionalInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="notes">{t('form.notes')}</Label>
                <textarea
                  id="notes"
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={t('form.notesPlaceholder')}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('form.summary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm py-1">
                <span>{t('form.subtotal')}</span>
                <span>
                  {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                    subTotal,
                  )}
                </span>
              </div>

              <div className="flex items-center gap-4 py-1">
                <span className="text-sm">{t('form.taxRate')}</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  className="w-20 ml-auto h-8"
                  value={formData.taxRate}
                  onChange={(e) => setFormData({ ...formData, taxRate: Number(e.target.value) })}
                />
              </div>

              {taxAmount > 0 && (
                <div className="flex justify-between text-sm py-1 text-muted-foreground">
                  <span>{t('form.taxAmount')}</span>
                  <span>
                    {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                      taxAmount,
                    )}
                  </span>
                </div>
              )}

              <div className="border-t pt-4 flex justify-between font-bold text-lg">
                <span>{t('form.total')}</span>
                <span>
                  {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
                    total,
                  )}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-4 pb-12">
          <Button type="button" variant="outline" onClick={() => navigate('/invoices')}>
            {t('form.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? t('form.submitting') : t('form.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
}
